import React, { useState, useEffect } from 'react';
import {
  Clock,
  Play,
  Plus,
  Trash2,
  Edit2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Terminal,
  Layers,
  X,
  RefreshCw,
  GitBranch,
  Eye
} from 'lucide-react';
import { DataTable, type Column, type FilterConfig, type RowAction } from './DataTable';
import { api } from '../lib/api';
import { useOrg } from '../lib/org-context';

interface RunHistoryItem {
  id: string;
  timestamp: string;
  status: 'success' | 'failure' | 'recorded';
  log: string;
}

interface WorkflowSchedule {
  id: string;
  name: string;
  workflowId: string;
  workflowName: string;
  cronExpression: string;
  enabled: boolean;
  targetBranch: string;
  notifySlack: boolean;
  notifyTelegram: boolean;
  lastRunStatus: 'success' | 'failure' | 'running' | 'recorded' | 'idle';
  lastRunTime?: string;
  nextRunTime?: string;
  createdAt: string;
  runHistory: RunHistoryItem[];
}

// Clickable cron presets — each is a real 5-field cron expression (the old
// 'hourly'/'daily' words were unparseable). Clicking a chip sets the field;
// the custom input below covers anything not listed.
const AVAILABLE_WORKFLOWS = [
  { id: 'sec_scan', name: 'Auto PR Security Scan & Compliance Audit', desc: 'Checks pull requests for security exploits, secrets, and injection.' },
  { id: 'quality_sweep', name: 'Weekly Code Quality Sweep', desc: 'Runs cyclomatic complexity checks and identifies duplicate/unoptimized code.' },
  { id: 'dependency_audit', name: 'Daily Dependency & SBOM Audit', desc: 'Validates package.json dependencies and compiles security vulnerabilities.' },
  { id: 'refinement_loop', name: 'Interactive PR Refinement Loop', desc: 'Drives the interactive NIM patch correction feedback loop autonomously.' }
];

const CRON_PRESETS: { value: string; label: string }[] = [
  { value: '*/5 * * * *', label: 'Every 5 min' },
  { value: '0 * * * *', label: 'Every hour' },
  { value: '0 0 * * *', label: 'Daily @ midnight' },
  { value: '0 2 * * *', label: 'Daily @ 2 AM' },
  { value: '0 9 * * 1-5', label: 'Weekdays @ 9 AM' },
  { value: '0 8 * * 1', label: 'Weekly Mon @ 8 AM' },
];

// Mirrors the Worker's isValidCron so the submit button stays disabled until
// the field parses as 5-field cron.
const CRON_FIELD = '(\\*|\\d+|\\*\\/\\d+|\\d+-\\d+|\\d+(,\\d+)*)';
const CRON_RE = new RegExp(`^${CRON_FIELD}(\\s+${CRON_FIELD}){4}$`);
function isValidCron(expr: string): boolean {
  return CRON_RE.test(expr.trim());
}

export function ScheduleView() {
  const { current } = useOrg();
  const orgId = current?.orgId ?? null;

  const [schedules, setSchedules] = useState<WorkflowSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [currentId, setCurrentId] = useState('');
  
  // Form fields
  const [name, setName] = useState('');
  const [workflowId, setWorkflowId] = useState('sec_scan');
  const [cronExpression, setCronExpression] = useState('0 * * * *');
  const [targetBranch, setTargetBranch] = useState('main');
  const [notifySlack, setNotifySlack] = useState(true);
  const [notifyTelegram, setNotifyTelegram] = useState(false);
  
  // Selected schedule for log viewer
  const [selectedScheduleId, setSelectedScheduleId] = useState<string>('');
  const [selectedRunIndex, setSelectedRunIndex] = useState<number>(0);
  const [isRefreshingLogs, setIsRefreshingLogs] = useState(false);

  // Fetch all schedules
  const fetchSchedules = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await api<WorkflowSchedule[]>('/api/schedules');
      setSchedules(data);
      if (data.length > 0 && !selectedScheduleId) {
        setSelectedScheduleId(data[0].id);
        setSelectedRunIndex(0);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load schedules.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Refetch when the selected org changes so Org B never shows Org A's rows.
    if (!orgId) { setSchedules([]); setLoading(false); return; }
    // Clear the log viewer selection too — the id belongs to the previous org.
    setSelectedScheduleId('');
    setSelectedRunIndex(0);
    fetchSchedules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  // Handle immediate run simulation
  const handleRunNow = async (id: string) => {
    // Optimistically show 'running' while the request is in flight.
    setSchedules(prev => prev.map(s => s.id === id ? { ...s, lastRunStatus: 'running' } : s));
    try {
      const updated = await api<WorkflowSchedule>(`/api/schedules/${id}/run`, { method: 'POST' });
      setSchedules(prev => prev.map(s => s.id === id ? updated : s));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not run schedule.');
      fetchSchedules();
    }
  };

  // Handle toggling enable/disable
  const handleToggle = async (id: string) => {
    try {
      const updated = await api<WorkflowSchedule>(`/api/schedules/${id}/toggle`, { method: 'POST' });
      setSchedules(prev => prev.map(s => s.id === id ? updated : s));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Network error toggling state.');
    }
  };

  // Handle delete
  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this automated workflow schedule?')) return;
    try {
      await api(`/api/schedules/${id}`, { method: 'DELETE' });
      setSchedules(prev => prev.filter(s => s.id !== id));
      if (selectedScheduleId === id) {
        setSelectedScheduleId('');
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Network error deleting schedule.');
    }
  };

  // Open Create modal
  const openCreateModal = () => {
    setModalMode('create');
    setName('');
    setWorkflowId('sec_scan');
    setCronExpression('0 * * * *');
    setTargetBranch('main');
    setNotifySlack(true);
    setNotifyTelegram(false);
    setError('');
    setModalOpen(true);
  };

  // Open Edit modal
  const openEditModal = (sch: WorkflowSchedule) => {
    setModalMode('edit');
    setCurrentId(sch.id);
    setName(sch.name);
    setWorkflowId(sch.workflowId);
    setCronExpression(sch.cronExpression);
    setTargetBranch(sch.targetBranch);
    setNotifySlack(sch.notifySlack);
    setNotifyTelegram(sch.notifyTelegram);
    setError('');
    setModalOpen(true);
  };

  // Save / Update form handler
  const handleSaveSchedule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      alert('Please enter a schedule name');
      return;
    }

    const matchedWf = AVAILABLE_WORKFLOWS.find(w => w.id === workflowId);
    const workflowName = matchedWf ? matchedWf.name : 'Automated Workflow';

    const payload = {
      name,
      workflowId,
      workflowName,
      cronExpression: cronExpression.trim(),
      targetBranch,
      notifySlack,
      notifyTelegram
    };

    try {
      const url = modalMode === 'create' ? '/api/schedules' : `/api/schedules/${currentId}`;
      const method = modalMode === 'create' ? 'POST' : 'PUT';

      await api(url, { method, body: JSON.stringify(payload) });

      setModalOpen(false);
      fetchSchedules();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection failure saving schedule details.');
    }
  };

  const selectedSch = schedules.find(s => s.id === selectedScheduleId);

  const scheduleColumns: Column<WorkflowSchedule>[] = [
    {
      key: 'name',
      header: 'Schedule',
      width: '28%',
      render: (s) => (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="font-bold text-sm text-[#191919] tracking-tight">{s.name}</span>
            <button
              type="button"
              onClick={() => handleToggle(s.id)}
              className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border cursor-pointer ${
                s.enabled
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : 'bg-slate-100 text-slate-500 border-slate-200'
              }`}
              title={s.enabled ? 'Click to disable' : 'Click to enable'}
            >
              {s.enabled ? 'Active' : 'Paused'}
            </button>
          </div>
          <div className="flex items-center gap-1 text-xs text-slate-500 font-mono">
            <Layers className="w-3.5 h-3.5 text-purple-500" />
            {s.workflowName}
          </div>
        </div>
      ),
    },
    { key: 'cron', header: 'Cron', width: '14%', render: (s) => <span className="text-xs font-mono text-slate-700">{s.cronExpression}</span> },
    { key: 'branch', header: 'Branch', width: '12%', render: (s) => <span className="inline-flex items-center gap-1 text-xs text-slate-600"><GitBranch className="w-3.5 h-3.5 text-slate-400" />{s.targetBranch}</span> },
    {
      key: 'notify',
      header: 'Notify',
      width: '14%',
      render: (s) => (
        <span className="flex gap-1">
          {s.notifySlack && <span className="bg-orange-50 text-orange-600 px-1 rounded text-[10px] border border-orange-100">Slack</span>}
          {s.notifyTelegram && <span className="bg-blue-50 text-blue-600 px-1 rounded text-[10px] border border-blue-100">Telegram</span>}
          {!s.notifySlack && !s.notifyTelegram && <span className="text-xs text-slate-400">Silent</span>}
        </span>
      ),
    },
    {
      key: 'lastrun',
      header: 'Last run',
      width: '16%',
      render: (s) => (
        <div className="flex items-center gap-1.5 text-xs">
          {s.lastRunStatus === 'running' ? (
            <span className="text-purple-600 font-semibold flex items-center gap-1"><RefreshCw className="w-3.5 h-3.5 animate-spin" />Running</span>
          ) : s.lastRunStatus === 'success' ? (
            <span className="text-emerald-600 font-semibold flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />Success</span>
          ) : s.lastRunStatus === 'failure' ? (
            <span className="text-red-600 font-semibold flex items-center gap-1"><XCircle className="w-3.5 h-3.5" />Failed</span>
          ) : s.lastRunStatus === 'recorded' ? (
            <span className="text-amber-600 font-semibold flex items-center gap-1"><Play className="w-3.5 h-3.5" />Recorded (no executor)</span>
          ) : (
            <span className="text-slate-400 italic">Never</span>
          )}
          {s.lastRunTime && <span className="text-[10px] text-slate-400 font-mono">{new Date(s.lastRunTime).toLocaleTimeString()}</span>}
        </div>
      ),
    },
  ];

  const scheduleFilters: FilterConfig<WorkflowSchedule>[] = [
    {
      key: 'enabled',
      label: 'Status',
      options: [{ value: 'active', label: 'Active' }, { value: 'paused', label: 'Paused' }],
      predicate: (s, v) => (v === 'active' ? s.enabled : !s.enabled),
    },
    {
      key: 'lastrun',
      label: 'Last run',
      options: [
        { value: 'success', label: 'Success' },
        { value: 'failure', label: 'Failure' },
        { value: 'running', label: 'Running' },
        { value: 'recorded', label: 'Recorded' },
        { value: 'idle', label: 'Never' },
      ],
      predicate: (s, v) => s.lastRunStatus === v,
    },
  ];

  const scheduleActions: RowAction<WorkflowSchedule>[] = [
    { key: 'logs', label: 'View logs', icon: Eye, onClick: (s) => { setSelectedScheduleId(s.id); setSelectedRunIndex(0); }, title: 'Logs' },
    { key: 'run', label: 'Run now', icon: Play, onClick: (s) => handleRunNow(s.id), disabled: (s) => s.lastRunStatus === 'running', title: 'Run now' },
    { key: 'edit', label: 'Edit schedule', icon: Edit2, onClick: (s) => openEditModal(s), title: 'Edit' },
    { key: 'delete', label: 'Delete schedule', icon: Trash2, onClick: (s) => handleDelete(s.id), tone: 'danger', title: 'Delete' },
  ];

  return (
    <div className="space-y-6" id="scheduler-container">
      {/* Top Banner Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-[#EDEDEB] pb-5" id="scheduler-header">
        <div>
          <h2 className="text-xl font-bold text-[#191919] flex items-center gap-2">
            <Clock className="w-5 h-5 text-purple-600 animate-pulse" />
            Automated Workflow Scheduler
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Create, toggle, and configure cron triggers to run continuous PR audits, SBOM audits, and refinement pipelines.
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-1.5 bg-[#191919] hover:bg-slate-800 text-white text-xs font-semibold px-4 py-2 rounded-lg transition duration-100 shadow-xs cursor-pointer self-start md:self-center"
          id="btn-add-schedule"
        >
          <Plus className="w-4 h-4" />
          Add Schedule
        </button>
      </div>

      {error && !modalOpen && (
        <div className="flex items-start gap-2 border border-red-200 bg-red-50 text-red-700 text-xs rounded-lg px-3 py-2" role="alert">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Main Grid: Left Schedules List, Right Console Logs */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" id="scheduler-grid">
        
        {/* Left Column - Schedules List (7 cols) */}
        <div className="lg:col-span-7 space-y-3" id="schedules-list-column">
          <DataTable
            rows={schedules}
            columns={scheduleColumns}
            rowKey={(s) => s.id}
            loading={loading && schedules.length === 0}
            searchKeys={[(s) => s.name, (s) => s.workflowName]}
            searchPlaceholder="Search schedules…"
            filters={scheduleFilters}
            actions={scheduleActions}
            pageSize={10}
            skeletonCols={['28%', '14%', '12%', '14%', '16%']}
            emptyState={
              <>
                <Clock className="w-5 h-5 mx-auto mb-2 opacity-60" />
                No scheduled workflows yet. Click <strong>Add Schedule</strong>.
              </>
            }
            toolbar={
              <>
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">({schedules.length})</span>
                <button
                  onClick={fetchSchedules}
                  className="text-purple-600 hover:text-purple-800 flex items-center gap-1 text-[10px] font-mono cursor-pointer"
                >
                  <RefreshCw className="w-3 h-3" /> Refresh
                </button>
              </>
            }
          />
        </div>

        {/* Right Column - Console Log output & Run Histories (5 cols) */}
        <div className="lg:col-span-5 flex flex-col h-full space-y-4" id="log-viewer-column">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center justify-between">
            <span className="flex items-center gap-1.5">
              <Terminal className="w-4 h-4 text-purple-500" />
              Workflow Terminal Logs
            </span>
          </div>

          <div className="border border-slate-200 rounded-xl bg-slate-900 shadow-md text-slate-100 flex-1 flex flex-col overflow-hidden min-h-[480px]">
            {/* Console Header */}
            <div className="bg-slate-950 px-4 py-2.5 flex items-center justify-between border-b border-slate-800 text-xs text-slate-400 font-mono">
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
                <span className="ml-1 text-[11px] text-slate-400 max-w-[180px] truncate">
                  {selectedSch ? `${selectedSch.workflowId}.log` : 'nim-scheduler.log'}
                </span>
              </div>
              <div className="text-[10px] text-slate-500 bg-slate-900 px-1.5 py-0.5 rounded">
                UTF-8
              </div>
            </div>

            {selectedSch ? (
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* Run list tabs inside console */}
                <div className="bg-slate-950/40 border-b border-slate-800 px-3 py-1.5 flex gap-1.5 overflow-x-auto scrollbar-none">
                  {(!selectedSch.runHistory || selectedSch.runHistory.length === 0) ? (
                    <div className="text-[10px] text-slate-500 font-mono italic px-2 py-0.5">
                      No run history available
                    </div>
                  ) : (
                    selectedSch.runHistory.map((run, index) => (
                      <button
                        key={run.id}
                        onClick={() => setSelectedRunIndex(index)}
                        className={`text-[10px] font-mono px-2 py-1 rounded transition whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
                          selectedRunIndex === index 
                            ? 'bg-slate-800 text-white font-bold' 
                            : 'text-slate-400 hover:bg-slate-800/40 hover:text-slate-200'
                        }`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${run.status === 'success' ? 'bg-emerald-500' : run.status === 'recorded' ? 'bg-amber-500' : 'bg-red-500'}`} />
                        {new Date(run.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </button>
                    ))
                  )}
                </div>

                {/* Log terminal box */}
                <div className="flex-1 p-4 overflow-y-auto font-mono text-[11px] space-y-2 leading-relaxed bg-[#111622] text-[#e2e8f0]">
                  {selectedSch.lastRunStatus === 'running' && selectedRunIndex === 0 ? (
                    <div className="space-y-2 text-purple-300">
                      <p className="animate-pulse">[INFO] Initiating connection to repository environment...</p>
                      <p className="animate-pulse">[NIM AUTO] Spawning pipeline thread {selectedSch.id}...</p>
                      <div className="flex items-center gap-2 mt-2 font-semibold">
                        <RefreshCw className="w-3.5 h-3.5 animate-spin text-purple-400" />
                        <span>Continuous compliance tests running...</span>
                      </div>
                    </div>
                  ) : selectedSch.runHistory && selectedSch.runHistory[selectedRunIndex] ? (
                    <pre className="whitespace-pre-wrap font-mono leading-relaxed select-text">
                      {selectedSch.runHistory[selectedRunIndex].log}
                    </pre>
                  ) : (
                    <div className="text-slate-400 text-center py-20 italic">
                      <p className="font-semibold text-slate-500 mb-1">Log Empty</p>
                      <p className="text-[10px]">Execute "Run Now" to spawn and monitor simulated workflow telemetry logs.</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-6 text-center text-slate-500 font-mono text-xs">
                <Terminal className="w-8 h-8 text-slate-700 mb-2 animate-bounce" />
                <p>Select a schedule cards to trace execution outputs and run telemetry.</p>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Schedule Configuration Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-[#191919]/60 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto animate-fadeIn" id="schedule-modal-overlay">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-lg overflow-hidden flex flex-col animate-scaleUp" id="schedule-modal-content">
            
            {/* Header */}
            <div className="px-6 py-4.5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
              <div className="space-y-0.5">
                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-purple-600" />
                  {modalMode === 'create' ? 'Create Automated Schedule' : 'Edit Workflow Schedule'}
                </h3>
                <p className="text-[11px] text-slate-500">
                  Configure cron rules, notification pathways, and execution scopes.
                </p>
              </div>
              <button 
                onClick={() => setModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1.5 hover:bg-slate-100 rounded-full transition cursor-pointer"
                type="button"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSaveSchedule} className="p-6 space-y-4">

              {error && (
                <div className="flex items-start gap-2 border border-red-200 bg-red-50 text-red-700 text-xs rounded-lg px-3 py-2" role="alert">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {/* Schedule Name */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700 block">
                  Schedule Title Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Nightly Compliance Audit Loop"
                  className="w-full text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 outline-none focus:ring-1 focus:ring-purple-500 focus:bg-white transition"
                  required
                />
              </div>

              {/* Target Workflow Dropdown */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700 block">
                  Select Target Pipeline
                </label>
                <select
                  value={workflowId}
                  onChange={(e) => setWorkflowId(e.target.value)}
                  className="w-full text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 outline-none focus:ring-1 focus:ring-purple-500 focus:bg-white transition"
                >
                  {AVAILABLE_WORKFLOWS.map(wf => (
                    <option key={wf.id} value={wf.id}>
                      {wf.name}
                    </option>
                  ))}
                </select>
                <span className="text-[10px] text-slate-400 leading-normal block">
                  {AVAILABLE_WORKFLOWS.find(w => w.id === workflowId)?.desc}
                </span>
              </div>

              {/* Interval & Branch Configuration */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5 col-span-2">
                  <label className="text-xs font-semibold text-slate-700 block">
                    Cron Schedule
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {CRON_PRESETS.map(p => {
                      const active = cronExpression.trim() === p.value;
                      return (
                        <button
                          key={p.value}
                          type="button"
                          onClick={() => setCronExpression(p.value)}
                          className={`text-[11px] font-medium px-2.5 py-1.5 rounded-lg border transition cursor-pointer ${
                            active
                              ? 'bg-purple-600 text-white border-purple-600'
                              : 'bg-white text-slate-600 border-slate-200 hover:border-purple-300 hover:text-purple-700'
                          }`}
                        >
                          {p.label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={cronExpression}
                      onChange={(e) => setCronExpression(e.target.value)}
                      placeholder="custom 5-field cron, e.g. */30 9-17 * * 1-5"
                      className="flex-1 font-mono text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 outline-none focus:ring-1 focus:ring-purple-500 focus:bg-white transition"
                    />
                    {isValidCron(cronExpression) ? (
                      <span className="flex items-center gap-1 text-[10px] font-semibold text-emerald-600 whitespace-nowrap">
                        <CheckCircle2 className="w-3.5 h-3.5" /> valid
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-[10px] font-semibold text-red-500 whitespace-nowrap">
                        <AlertTriangle className="w-3.5 h-3.5" /> invalid
                      </span>
                    )}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-700 block">
                    Repository Branch Scope
                  </label>
                  <input
                    type="text"
                    value={targetBranch}
                    onChange={(e) => setTargetBranch(e.target.value)}
                    placeholder="e.g. main"
                    className="w-full text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 outline-none focus:ring-1 focus:ring-purple-500 focus:bg-white transition"
                    required
                  />
                </div>
              </div>

              {/* Alerts & Communications */}
              <div className="space-y-2 pt-2 border-t border-slate-100">
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                  Dispatches & Alerts Channels
                </span>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={notifySlack}
                      onChange={(e) => setNotifySlack(e.target.checked)}
                      className="rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-xs text-slate-600 font-medium">Slack Notifications</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={notifyTelegram}
                      onChange={(e) => setNotifyTelegram(e.target.checked)}
                      className="rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-xs text-slate-600 font-medium">Telegram channel alert</span>
                  </label>
                </div>
              </div>

              {/* Modal Actions */}
              <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-2 sticky bottom-0 bg-white">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-semibold rounded-lg transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!isValidCron(cronExpression)}
                  className="px-4 py-2 bg-[#191919] hover:bg-slate-800 text-white text-xs font-semibold rounded-lg shadow-sm transition cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {modalMode === 'create' ? 'Create Schedule' : 'Save Changes'}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

    </div>
  );
}
